import React, { useCallback, useEffect, useState } from "react";
import { api, API, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, CalendarDays, Ban, UserPlus, Loader2, Trash2,
  BadgeEuro, XCircle, CheckCircle2,
} from "lucide-react";

const DOW = ["L", "M", "M", "J", "V", "S", "D"];
const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const monthLabel = (m) => {
  const [y, mm] = m.split("-");
  return new Date(Number(y), Number(mm) - 1, 1)
    .toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
};
const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const STRIPES = {
  backgroundImage:
    "repeating-linear-gradient(45deg, #868E96 0 4px, #ADB5BD 4px 8px)",
};

const cellClass = (c) => {
  if (c.status === "blocked") return "bg-[#868E96]";
  if (c.status === "inactive") return "bg-[#DEE2E6]";
  if (c.status === "booked") {
    if (c.booking_status === "arrived") return "bg-[#FFD700]";
    return (c.payment_status || "").startsWith("paid") ? "bg-[#059669]" : "bg-[#E60000]";
  }
  return "bg-white hover:bg-[#E7F5FF]";
};

export const ReservationCalendar = ({ labels, onChanged }) => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);            // {unit, cell}
  const [busy, setBusy] = useState(false);
  const [block, setBlock] = useState({ date_to: "", reason: "" });
  const [manual, setManual] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/chef/minisite/calendar?month=${month}`);
      setData(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const open = (unit, cell) => {
    setSel({ unit, cell });
    setBlock({ date_to: addDays(cell.date, 1), reason: "" });
    setManual(null);
  };

  const refresh = async () => { await load(); onChanged?.(); };

  const doBlock = async () => {
    setBusy(true);
    try {
      await api.post("/chef/minisite/blocks", {
        unit_id: sel.unit.id, date_from: sel.cell.date,
        date_to: block.date_to, reason: block.reason,
      });
      toast.success(`${sel.unit.label} fermé`);
      setSel(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doUnblock = async () => {
    setBusy(true);
    try {
      await api.delete(`/chef/minisite/blocks/${sel.cell.block_id}`);
      toast.success("Fermeture levée");
      setSel(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doManual = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/chef/minisite/bookings", {
        unit_id: sel.unit.id, date_from: manual.date_from, date_to: manual.date_to,
        guests: parseInt(manual.guests) || 1, client_nom: manual.client_nom,
        client_email: manual.client_email, client_telephone: manual.client_telephone,
        payment_status: manual.payment_status,
        price_override: manual.price_override === "" ? null : parseFloat(manual.price_override),
        send_email: !!manual.client_email,
      });
      toast.success(`Réservation ${r.booking.reference} enregistrée`);
      setSel(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const collect = async () => {
    setBusy(true);
    try {
      await api.patch(`/chef/minisite/bookings/${sel.cell.booking_id}/payment`);
      toast.success("Encaissement enregistré");
      setSel(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/chef/minisite/bookings/${sel.cell.booking_id}/cancel`);
      toast.success("Réservation annulée — les dates sont libérées");
      setSel(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-sm text-[#868E96]" data-testid="cal-loading">Chargement du calendrier…</div>;

  const st = data.stats || {};
  const c = sel?.cell;

  return (
    <div data-testid="cr-calendar">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex items-center border border-[#DEE2E6]">
          <button onClick={() => setMonth(data.prev_month)} data-testid="cal-prev"
                  className="h-10 w-10 grid place-items-center hover:bg-[#F1F3F5]">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={() => setMonth(new Date().toISOString().slice(0, 7))} data-testid="cal-today"
                  className="h-10 px-3 text-[11px] uppercase tracking-wider font-bold hover:bg-[#F1F3F5]">
            Ce mois
          </button>
          <button onClick={() => setMonth(data.next_month)} data-testid="cal-next"
                  className="h-10 w-10 grid place-items-center hover:bg-[#F1F3F5]">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="font-display font-black text-xl tracking-tight capitalize" data-testid="cal-month">
          {monthLabel(data.month)}
        </div>
        <div className="flex flex-wrap gap-4 text-xs ml-auto" data-testid="cal-stats">
          <span><b className="text-lg">{st.occupancy_pct}%</b> d'occupation</span>
          <span><b className="text-lg">{st.arrivals}</b> arrivée(s)</span>
          <span><b className="text-lg">{eur(st.revenue)}</b> réservés</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-wider mb-2" data-testid="cal-legend">
        {[
          { c: "bg-white border border-[#DEE2E6]", l: "libre" },
          { c: "bg-[#E60000]", l: "à régler" },
          { c: "bg-[#059669]", l: "payé" },
          { c: "bg-[#FFD700]", l: "sur place" },
          { c: "bg-[#868E96]", l: "fermé" },
        ].map((x) => (
          <span key={x.l} className="flex items-center gap-1">
            <span className={`inline-block h-3 w-3 ${x.c}`} />{x.l}
          </span>
        ))}
        <span className="text-[#868E96] normal-case tracking-normal">
          Cliquez sur une case : voir la réservation, fermer l'unité ou saisir une réservation.
        </span>
      </div>

      <div className="overflow-x-auto border border-[#DEE2E6] bg-white inline-block max-w-full align-top">
        <table className="border-collapse w-max">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 bg-[#F8F9FA] border-b border-r border-[#DEE2E6]
                             px-2 py-1 text-left text-[10px] uppercase tracking-wider min-w-[130px]">
                {labels?.units || "Unités"}
              </th>
              {data.days.map((d) => (
                <th key={d.date} className={`border-b border-[#DEE2E6] w-[26px] text-[9px] font-bold py-1 ${
                  d.dow >= 5 ? "bg-[#F1F3F5]" : "bg-[#F8F9FA]"} ${d.today ? "text-[#E60000]" : "text-[#495057]"}`}>
                  <div>{DOW[d.dow]}</div>
                  <div>{d.day}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.units.map((u) => (
              <tr key={u.id}>
                <th className="sticky left-0 z-10 bg-white border-b border-r border-[#DEE2E6]
                               px-2 py-1 text-left text-[11px] font-bold whitespace-nowrap">
                  {u.label}
                  <span className="ml-1 text-[9px] font-normal text-[#868E96]">{eur(u.price_per_night)}</span>
                </th>
                {u.cells.map((cell) => (
                  <td key={cell.date} className="border-b border-r border-[#F1F3F5] p-0">
                    <button onClick={() => open(u, cell)} data-testid={`cal-cell-${u.id}-${cell.date}`}
                            title={cell.status === "booked"
                              ? `${cell.reference} — ${cell.client_nom}`
                              : cell.status === "blocked" ? `Fermé : ${cell.reason || "sans motif"}` : "Libre"}
                            style={cell.status === "blocked" ? STRIPES : undefined}
                            className={`h-7 w-[26px] block transition-colors ${cellClass(cell)} ${
                              cell.arrival ? "border-l-2 border-l-[#0A0A0A]" : ""}`}>
                      {cell.arrival && (
                        <span className="text-[8px] font-black text-white mix-blend-difference">
                          {(cell.client_nom || "?").slice(0, 1).toUpperCase()}
                        </span>
                      )}
                    </button>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---------- Fiche de la case ---------- */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-lg" data-testid="cal-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-black tracking-tight">
              {sel?.unit.label} · {c?.date}
            </DialogTitle>
          </DialogHeader>

          {c?.status === "booked" && (
            <div className="space-y-3" data-testid="cal-booking">
              <div className="grid grid-cols-[110px_1fr] gap-3 items-start">
                <img src={`${API}/public/bookings/${c.qr_token}/qr.png`} alt="QR"
                     className="w-[100px] h-[100px] object-contain border border-[#DEE2E6]" />
                <div className="text-sm">
                  <div className="font-display font-black text-lg leading-none">{c.client_nom}</div>
                  <div className="font-mono text-[11px] tracking-[0.15em] mt-1">{c.reference}</div>
                  <div className="text-[#495057] mt-1">
                    Du {c.date_from} au {c.date_to} · {eur(c.total)}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className={`text-[9px] uppercase font-bold px-2 py-0.5 ${
                      (c.payment_status || "").startsWith("paid") ? "bg-[#059669] text-white" : "bg-[#E60000] text-white"}`}>
                      {c.payment_status === "paid" ? "payé en ligne"
                        : c.payment_status === "paid_onsite" ? "payé sur place" : "à régler"}
                    </span>
                    <span className="text-[9px] uppercase font-bold px-2 py-0.5 bg-[#0A0A0A] text-white">
                      {c.booking_status}
                    </span>
                    {c.source === "chef" && (
                      <span className="text-[9px] uppercase font-bold px-2 py-0.5 bg-[#002FA7] text-white">
                        saisie réception
                      </span>
                    )}
                  </div>
                  {(c.extras || []).length > 0 && (
                    <div className="mt-2 text-[11px] text-[#495057]" data-testid="cal-booking-extras">
                      <b>À préparer :</b>{" "}
                      {c.extras.map((x) => `${x.label}${x.qty > 1 ? ` ×${x.qty}` : ""}`).join(" · ")}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {!(c.payment_status || "").startsWith("paid") && (
                  <Button onClick={collect} disabled={busy} data-testid="cal-collect"
                          className="bg-[#059669] text-white">
                    <BadgeEuro className="h-4 w-4 mr-1" />J'ai encaissé {eur(c.total)}
                  </Button>
                )}
                <Button variant="outline" onClick={cancel} disabled={busy} data-testid="cal-cancel"
                        className="text-[#E60000] border-[#E60000]">
                  <XCircle className="h-4 w-4 mr-1" />Annuler la réservation
                </Button>
              </div>
            </div>
          )}

          {c?.status === "blocked" && (
            <div className="space-y-3" data-testid="cal-blocked">
              <div className="text-sm">
                Fermé du <b>{c.date_from}</b> au <b>{c.date_to}</b>
                {c.reason ? <> — {c.reason}</> : null}
              </div>
              {c.source === "ical" ? (
                <div className="border border-[#002FA7] bg-[#E7F5FF] p-3 text-[11px]"
                     data-testid="cal-blocked-ical">
                  Ces dates viennent du calendrier <b>{c.platform || "externe"}</b> importé
                  automatiquement. Pour les libérer, annulez la réservation sur la plateforme :
                  la prochaine synchronisation les rouvrira ici.
                </div>
              ) : (
                <Button onClick={doUnblock} disabled={busy} data-testid="cal-unblock">
                  <Trash2 className="h-4 w-4 mr-1" />Lever la fermeture
                </Button>
              )}
            </div>
          )}

          {(c?.status === "free" || c?.status === "inactive") && !manual && (
            <div className="space-y-4" data-testid="cal-free">
              <div className="border border-[#DEE2E6] p-3">
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] flex items-center gap-1">
                  <Ban className="h-3 w-3" />Fermer cette unité
                </div>
                <div className="grid sm:grid-cols-2 gap-2 mt-2">
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Jusqu'au (exclu)</Label>
                    <Input type="date" value={block.date_to} data-testid="cal-block-to"
                           onChange={(e) => setBlock({ ...block, date_to: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Motif</Label>
                    <Input value={block.reason} placeholder="Travaux, entretien…" data-testid="cal-block-reason"
                           onChange={(e) => setBlock({ ...block, reason: e.target.value })} />
                  </div>
                </div>
                <Button onClick={doBlock} disabled={busy} className="mt-2" data-testid="cal-block-save">
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Ban className="h-4 w-4 mr-1" />}
                  Fermer ces dates
                </Button>
              </div>
              <Button variant="outline" className="w-full" data-testid="cal-manual-open"
                      onClick={() => setManual({
                        date_from: c.date, date_to: addDays(c.date, 1), guests: 2,
                        client_nom: "", client_email: "", client_telephone: "",
                        payment_status: "pending", price_override: "",
                      })}>
                <UserPlus className="h-4 w-4 mr-1" />Saisir une réservation (téléphone / comptoir)
              </Button>
            </div>
          )}

          {manual && (
            <div className="space-y-3" data-testid="cal-manual-form">
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Arrivée</Label>
                  <Input type="date" value={manual.date_from} data-testid="cal-manual-from"
                         onChange={(e) => setManual({ ...manual, date_from: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Départ</Label>
                  <Input type="date" value={manual.date_to} data-testid="cal-manual-to"
                         onChange={(e) => setManual({ ...manual, date_to: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Nom du client</Label>
                  <Input value={manual.client_nom} data-testid="cal-manual-nom"
                         onChange={(e) => setManual({ ...manual, client_nom: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Personnes</Label>
                  <Input type="number" min="1" value={manual.guests} data-testid="cal-manual-guests"
                         onChange={(e) => setManual({ ...manual, guests: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Email (facultatif)</Label>
                  <Input value={manual.client_email} placeholder="pour envoyer le QR code"
                         data-testid="cal-manual-email"
                         onChange={(e) => setManual({ ...manual, client_email: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Téléphone</Label>
                  <Input value={manual.client_telephone} data-testid="cal-manual-tel"
                         onChange={(e) => setManual({ ...manual, client_telephone: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Prix négocié (facultatif)</Label>
                  <Input type="number" step="0.01" value={manual.price_override}
                         placeholder="laisser vide = tarif du jour" data-testid="cal-manual-price"
                         onChange={(e) => setManual({ ...manual, price_override: e.target.value })} />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider">Paiement</Label>
                  <div className="flex gap-1">
                    {[{ k: "pending", l: "à régler" }, { k: "paid_onsite", l: "déjà payé" }].map((p) => (
                      <button key={p.k} type="button" data-testid={`cal-manual-pay-${p.k}`}
                              onClick={() => setManual({ ...manual, payment_status: p.k })}
                              className={`h-10 px-3 text-[10px] uppercase font-bold border ${
                                manual.payment_status === p.k ? "bg-[#0A0A0A] text-white" : "bg-white text-[#868E96] border-[#DEE2E6]"}`}>
                        {p.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={doManual} disabled={busy || !manual.client_nom} data-testid="cal-manual-save"
                        className="bg-[#002FA7] text-white">
                  {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                  Enregistrer la réservation
                </Button>
                <Button variant="outline" onClick={() => setManual(null)} data-testid="cal-manual-back">Retour</Button>
              </div>
              <div className="text-[11px] text-[#868E96] flex items-center gap-1">
                <CalendarDays className="h-3 w-3" />
                Un QR code est généré : le client peut le recevoir par email et le présenter à l'arrivée.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
